import React, { useMemo } from "react";
import DoorRenderer from "./DoorRenderer";
import WindowRenderer from "./WindowRenderer";
import PanoramicWindowRenderer from "./PanoramicWindowRenderer";
import OutletRenderer from "./OutletRenderer";
import Outlet2Renderer from "./Outlet2Renderer";
import SwitchRenderer from "./SwitchRenderer";
import SwitchDoubleRenderer from "./SwitchDoubleRenderer";
import RadiatorRenderer from "./RadiatorRenderer";
import KitchenElementRenderer from "./KitchenElementRenderer";
import HeaterRenderer from "./HeaterRenderer";
import ToiletRenderer from "./ToiletRenderer";
import WaterHeaterRenderer from "./WaterHeaterRenderer";
import HandBasinRenderer from "./HandBasinRenderer";
import AirConditionerInternalRenderer from "./AirConditionerInternalRenderer";
import AirConditionerExternalRenderer from "./AirConditionerExternalRenderer";
import ShowerRenderer from "./ShowerRenderer";
import CustomElementRenderer from "./CustomElementRenderer";
import { ReactComponent as WallSvg } from "../../Assets/wall.svg";
import {
  OBJECT_TYPES,
  SELECTED_ELEMENT_COLOR,
  WALL_THICKNESS_M_RENDER,
  defaultObjectSizes,
  PLACEMENT_HIGHLIGHT_FILL_COLOR,
  PLACEMENT_NOT_ALLOWED_FILL_COLOR,
  GRID_CELL_SIZE_M,
  FRAMELESS_GRID_CELL_SIZE_M,
  SANITARY_ELEMENT_TYPES,
} from "../configuratorConstants";
import {
  subtractVectors,
  distance,
  rotatePoint,
  addVectors,
} from "../configuratorUtils";

const DockingJointRenderer = ({
  joint,
  allJoints,
  scale,
  isSelected,
  selectedElementId,
  onSelectJoint,
  onSelectElement,
  onContextMenu,
  isInPlacementMode,
  highlightForPlacement,
}) => {
  const isFrameless = joint.type === OBJECT_TYPES.FRAMELESS_PARTITION;
  const cellSize = isFrameless ? FRAMELESS_GRID_CELL_SIZE_M : GRID_CELL_SIZE_M;
  const cellSizePx = cellSize * scale;

  const { hasLeftNeighbor, hasRightNeighbor } = useMemo(() => {
    if (!allJoints || allJoints.length < 2) {
      return { hasLeftNeighbor: false, hasRightNeighbor: false };
    }

    const getEndpoints = (j) => {
      const center = { x: j.x, y: j.y };
      const halfWidthVec = { x: j.width / 2, y: 0 };
      const rotatedHalfWidth = rotatePoint(
        halfWidthVec,
        { x: 0, y: 0 },
        j.rotation || 0,
      );
      const p1 = subtractVectors(center, rotatedHalfWidth);
      const p2 = addVectors(center, rotatedHalfWidth);
      return { p1, p2 };
    };

    const { p1: myP1, p2: myP2 } = getEndpoints(joint);
    let left = false, right = false;
    const tolerance = 0.01;

    for (const otherJoint of allJoints) {
      if (otherJoint.id === joint.id) continue;
      if (Math.abs((otherJoint.rotation || 0) - (joint.rotation || 0)) > 1) continue;

      const { p1: otherP1, p2: otherP2 } = getEndpoints(otherJoint);

      if (distance(myP1, otherP2) < tolerance) left = true;
      if (distance(myP2, otherP1) < tolerance) right = true;
    }

    return { hasLeftNeighbor: left, hasRightNeighbor: right };
  }, [joint, allJoints]);

  const {
    id: jointId,
    width,
    displayMode = "double",
    elements = [],
    activeWallSegments = [],
    passageSegments = [],
  } = joint;

  const jointTransform = `translate(${joint.x * scale}, ${joint.y * scale}) rotate(${joint.rotation || 0})`;

  const isVerticalJoint = Math.abs(joint.rotation % 180) === 90;

  const jointWidthPx = width * scale;

  const wallThicknessPx = WALL_THICKNESS_M_RENDER * scale;

  const horizontalDoubleWallOffset = wallThicknessPx * 0.35;
  const horizontalSingleWallOffset = wallThicknessPx * 0.35;
  const verticalDoubleWallOffset = wallThicknessPx * 1;
  const verticalSingleWallOffset = wallThicknessPx * 1;

  let finalOffset;

  if (isFrameless) {
    finalOffset = 0;
  } else {
    if (displayMode === "double") {
      finalOffset = isVerticalJoint ? verticalDoubleWallOffset : horizontalDoubleWallOffset;
    } else if (displayMode === "single") {
      finalOffset = isVerticalJoint ? verticalSingleWallOffset : horizontalSingleWallOffset;
    } else {
      finalOffset = 0;
    }
  }

  const renderPhysicalWall = () => {
    if (displayMode === "passage") {
      return null;
    }

    const numSlots = Math.max(1, Math.round(width / cellSize));

    const renderWallTiles = (segmentsToDraw, y, height, keyPrefix) => {
      return segmentsToDraw.flatMap((seg, i) => {
        const wallTiles = [];
        let currentX = seg.start;
        const segmentEnd = seg.end;
        let tileIndex = 0;

        while (currentX < segmentEnd - 0.01) {
          const remainingWidth = segmentEnd - currentX;
          const tileWidth = Math.min(cellSizePx, remainingWidth);

          wallTiles.push(
            <WallSvg
              key={`${keyPrefix}-${i}-tile-${tileIndex++}`}
              x={currentX}
              y={y}
              width={tileWidth}
              height={height}
              preserveAspectRatio="none"
            />
          );
          currentX += tileWidth;
        }
        return wallTiles;
      });
    };

    if (displayMode === "double" && passageSegments && passageSegments.length > 0) {
      const segmentWidthPx = jointWidthPx / numSlots;
      const wallSegments = [];
      for (let i = 0; i < numSlots; i++) {
        if (!passageSegments.includes(i)) {
          wallSegments.push({
            start: i * segmentWidthPx,
            end: (i + 1) * segmentWidthPx
          });
        }
      }

      const gapBetweenWalls = 0.04 * scale;
      const yOffsetPositiveSide = gapBetweenWalls / 2;
      const yOffsetNegativeSide = -wallThicknessPx - gapBetweenWalls / 2;

      return (
        <g transform={`translate(${-jointWidthPx / 2}, 0)`}>
          {renderWallTiles(wallSegments, yOffsetPositiveSide, wallThicknessPx, 'passage-double-pos')}
          {renderWallTiles(wallSegments, yOffsetNegativeSide, wallThicknessPx, 'passage-double-neg')}
        </g>
      );
    }
    
    if (displayMode === "single" && passageSegments && passageSegments.length > 0) {
      const segmentWidthPx = jointWidthPx / numSlots;
      const wallSegments = [];
      for (let i = 0; i < numSlots; i++) {
        if (!passageSegments.includes(i)) {
          wallSegments.push({
            start: i * segmentWidthPx,
            end: (i + 1) * segmentWidthPx
          });
        }
      }
      return (
        <g transform={`translate(${-jointWidthPx / 2}, 0)`}>
          {renderWallTiles(wallSegments, -wallThicknessPx / 2, wallThicknessPx, 'passage-single')}
        </g>
      );
    }

    if (displayMode === "custom") {
        const segmentWidthPx = jointWidthPx / numSlots;
        const totalHeight = wallThicknessPx;
        const wallSegments = [];
        const intersectionTolerance = 0.001 * scale;

        activeWallSegments.forEach((segmentIndex) => {
            const segmentStart = segmentIndex * segmentWidthPx;
            const segmentEnd = segmentStart + segmentWidthPx;
            let hasDoor = false;
            elements.forEach((element) => {
                if (element.type === OBJECT_TYPES.DOOR) {
                    const availableWidth = jointWidthPx;
                    const doorPositionX = (element.positionOnSegment || 0.5) * availableWidth;
                    const doorWidthPx = (element.width || 0.9) * scale;
                    const doorStart = doorPositionX - doorWidthPx / 2;
                    const doorEnd = doorPositionX + doorWidthPx / 2;
                    const overlapStart = Math.max(doorStart, segmentStart);
                    const overlapEnd = Math.min(doorEnd, segmentEnd);
                    const overlapWidth = overlapEnd - overlapStart;
                    if (overlapWidth > Math.min(doorWidthPx * 0.3, segmentWidthPx * 0.3) - intersectionTolerance) {
                        hasDoor = true;
                    }
                }
            });
            if (!hasDoor) {
                wallSegments.push({ start: segmentStart, end: segmentEnd, index: segmentIndex });
            }
        });
        return (
            <g transform={`translate(${-jointWidthPx / 2}, 0)`}>
              {renderWallTiles(wallSegments, -totalHeight / 2, totalHeight, 'custom-wall')}
            </g>
        );
    }

    const calculateWallSegmentsForSide = (targetSide) => {
        let currentSegments = [{ start: finalOffset, end: jointWidthPx - finalOffset }];
        const intersectionTolerance = Math.max(0.1, 0.001 * scale);

        elements.forEach((opening) => {
            const elSide = opening.placementSide || 1;
            const isOpeningElement = opening.type === OBJECT_TYPES.DOOR || opening.type === OBJECT_TYPES.WINDOW || opening.type === OBJECT_TYPES.PANORAMIC_WINDOW;
            if (!isOpeningElement) return;

            let shouldCreateOpening = false;
            if (displayMode === "single") {
                shouldCreateOpening = opening.type === OBJECT_TYPES.DOOR || opening.type === OBJECT_TYPES.PANORAMIC_WINDOW;
            } else if (opening.type === OBJECT_TYPES.DOOR) {
                shouldCreateOpening = true;
            } else if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                // Панорамное окно создает проем на обеих сторонах
                shouldCreateOpening = true;
                console.log('🪟 [PANORAMIC WINDOW] Creating opening on side:', {
                    targetSide,
                    displayMode,
                    positionOnSegment: opening.positionOnSegment,
                    placementSide: elSide
                });
            } else if (isOpeningElement && opening.type !== OBJECT_TYPES.DOOR) {
                shouldCreateOpening = elSide !== targetSide;
            }

            if (shouldCreateOpening) {
                let openingWidthM;
                if (opening.type === OBJECT_TYPES.WINDOW) {
                    openingWidthM = 0.9;
                } else if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                    // Панорамное окно занимает 2 сэндвича
                    openingWidthM = GRID_CELL_SIZE_M * 2;
                    console.log('📏 [PANORAMIC WINDOW] Opening width calculation:', {
                        openingWidthM,
                        GRID_CELL_SIZE_M,
                        calculated: GRID_CELL_SIZE_M * 2,
                        elementWidth: opening.width,
                        defaultWidth: defaultObjectSizes[OBJECT_TYPES.PANORAMIC_WINDOW]?.width
                    });
                } else {
                    openingWidthM = opening.width || defaultObjectSizes[opening.type].width;
                }
                const openingWidthPx = openingWidthM * scale;
                const openingStartPx_abs = (opening.positionOnSegment * jointWidthPx) - openingWidthPx / 2;
                const openingEndPx_abs = openingStartPx_abs + openingWidthPx;
                
                if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                    console.log('✂️ [PANORAMIC WINDOW] Creating wall opening:', {
                        openingWidthM,
                        openingWidthPx,
                        jointWidthPx,
                        positionOnSegment: opening.positionOnSegment,
                        openingStartPx_abs,
                        openingEndPx_abs,
                        openingSpan: openingEndPx_abs - openingStartPx_abs
                    });
                }
                
                const newSegments = [];
                currentSegments.forEach((seg) => {
                    const hasOverlap = (openingEndPx_abs > seg.start + intersectionTolerance) && 
                                      (openingStartPx_abs < seg.end - intersectionTolerance);
                    
                    if (hasOverlap) {
                        if (openingStartPx_abs > seg.start + intersectionTolerance) {
                            newSegments.push({ start: seg.start, end: openingStartPx_abs });
                        }
                        if (openingEndPx_abs < seg.end - intersectionTolerance) {
                            newSegments.push({ start: openingEndPx_abs, end: seg.end });
                        }
                    } else {
                        newSegments.push(seg);
                    }
                });
                currentSegments = newSegments;
            }
        });

        const minSegmentWidth = 0.1;
        currentSegments = currentSegments.filter(seg => (seg.end - seg.start) >= minSegmentWidth);
        return currentSegments;
    };

    if (displayMode === 'single') {
        const singleWallSegments = calculateWallSegmentsForSide(1);
        const totalHeight = wallThicknessPx;
        return (
            <g transform={`translate(${-jointWidthPx / 2}, 0)`}>
                {renderWallTiles(singleWallSegments, -totalHeight / 2, totalHeight, 'single-wall')}
            </g>
        );
    }

    if (displayMode === 'double') {
        const gapBetweenWalls = 0.04 * scale;
        const yOffsetPositiveSide = gapBetweenWalls / 2;
        const yOffsetNegativeSide = -wallThicknessPx - gapBetweenWalls / 2;
        const positiveSideSegments = calculateWallSegmentsForSide(1);
        const negativeSideSegments = calculateWallSegmentsForSide(-1);
        return (
            <g transform={`translate(${-jointWidthPx / 2}, 0)`}>
                {renderWallTiles(positiveSideSegments, yOffsetPositiveSide, wallThicknessPx, 'pos-wall')}
                {renderWallTiles(negativeSideSegments, yOffsetNegativeSide, wallThicknessPx, 'neg-wall')}
            </g>
        );
    }
    
    return null;
  };

  const renderElements = () => {
    if (!elements || elements.length === 0) return null;
    return (
      <g>
        {elements.map((el) => {
          if (!el) return null;

          let availableWidth, positionX;
          if (el.type === OBJECT_TYPES.DOOR) {
            availableWidth = jointWidthPx;
            positionX = (el.positionOnSegment || 0.5) * availableWidth - availableWidth / 2;
          } else {
            availableWidth = jointWidthPx - 2 * finalOffset;
            positionX = (el.positionOnSegment || 0.5) * availableWidth - availableWidth / 2;
          }
          
          let placementSideFactor;
          if (el.type === OBJECT_TYPES.DOOR && displayMode === "double") {
            const openingDirection = el.openingDirection || "outward";
            placementSideFactor = openingDirection === "outward" ? -1 : 1;
          } else {
            placementSideFactor = el.placementSide || 1;
          }

          let positionY = 0;
          if (displayMode === "double") {
            const wallThicknessPx = WALL_THICKNESS_M_RENDER * scale;
            const gapBetweenWalls = 0.04 * scale;
            positionY =
              placementSideFactor * (wallThicknessPx / 2 + gapBetweenWalls / 2);
          } else if (displayMode === "single") {
            const wallThicknessPx = WALL_THICKNESS_M_RENDER * scale;
            if (SANITARY_ELEMENT_TYPES.includes(el.type)) {
              positionY = placementSideFactor * (wallThicknessPx / 2);
            }
          }

          const windowShouldFlip = displayMode === "double" && placementSideFactor === -1;
          let jointPosition = null;

          const jointRotation = joint.rotation || 0;
          const isVerticalJoint = Math.abs(jointRotation % 180) === 90;
          const orientation = isVerticalJoint ? 'v' : 'h';

          const commonProps = {
            element: el,
            scale,
            isSelected: selectedElementId === el.id,
            wallThickness: WALL_THICKNESS_M_RENDER,
            placementSideFactor: placementSideFactor,
            jointPosition: jointPosition,
            orientation: orientation,
            jointWidth: joint.width,
          };

          const renderers = {
            [OBJECT_TYPES.DOOR]: <DoorRenderer {...commonProps} />,
            [OBJECT_TYPES.WINDOW]: (
              <WindowRenderer
                {...commonProps}
                windowVisualNeedsFlip={windowShouldFlip}
              />
            ),
            [OBJECT_TYPES.PANORAMIC_WINDOW]: (
              <PanoramicWindowRenderer
                {...commonProps}
                windowVisualNeedsFlip={windowShouldFlip}
              />
            ),
            [OBJECT_TYPES.OUTLET]: <OutletRenderer {...commonProps} />,
            [OBJECT_TYPES.OUTLET_DOUBLE]: <Outlet2Renderer {...commonProps} />,
            [OBJECT_TYPES.SWITCH]: <SwitchRenderer {...commonProps} />,
            [OBJECT_TYPES.SWITCH_DOUBLE]: (
              <SwitchDoubleRenderer {...commonProps} />
            ),
            [OBJECT_TYPES.RADIATOR]: <RadiatorRenderer {...commonProps} />,
            [OBJECT_TYPES.KITCHEN_ELEMENT]: (
              <KitchenElementRenderer {...commonProps} />
            ),
            [OBJECT_TYPES.HEATER]: <HeaterRenderer {...commonProps} />,
            [OBJECT_TYPES.TOILET]: <ToiletRenderer {...commonProps} />,
            [OBJECT_TYPES.WATER_HEATER]: (
              <WaterHeaterRenderer {...commonProps} />
            ),
            [OBJECT_TYPES.HAND_BASIN]: <HandBasinRenderer {...commonProps} />,
            [OBJECT_TYPES.INTERNAL_AIR_CONDITIONER]: (
              <AirConditionerInternalRenderer {...commonProps} />
            ),
            [OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER]: (
              <AirConditionerExternalRenderer {...commonProps} />
            ),
            [OBJECT_TYPES.SHOWER]: <ShowerRenderer {...commonProps} />,
          };

          // Используем CustomElementRenderer для неизвестных типов
          const elementRenderer = renderers[el.type] || <CustomElementRenderer {...commonProps} />;

          return (
            <g
              key={el.id}
              transform={`translate(${positionX}, ${positionY})`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectElement(el.id);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onContextMenu(e, el.id, el.type);
              }}
              className="cursor-pointer"
          style={{ cursor: "pointer" }}
              data-object-id={el.id}
              data-object-type={el.type}
            >
              {elementRenderer}
            </g>
          );
        })}
      </g>
    );
  };
  
  let totalInteractionHeightPx;
  if (displayMode === 'double') {
    const gapBetweenWalls = 0.04 * scale;
    totalInteractionHeightPx = (WALL_THICKNESS_M_RENDER * scale * 2) + gapBetweenWalls;
  } else {
    totalInteractionHeightPx = WALL_THICKNESS_M_RENDER * scale;
  }

  let overlayFill = "none";
  if (isSelected && !selectedElementId) {
    overlayFill = "rgba(0, 123, 255, 0.3)";
  } else if (isInPlacementMode) {
    overlayFill = highlightForPlacement
      ? PLACEMENT_HIGHLIGHT_FILL_COLOR
      : PLACEMENT_NOT_ALLOWED_FILL_COLOR;
  }

  const availableWidthPx = jointWidthPx - 2 * finalOffset;
  const sandwichWidthM = availableWidthPx / scale;
  
  const renderJointSegments = () => {
    const numSlots = Math.max(1, Math.round(width / cellSize));
    const segmentWidthPx = jointWidthPx / numSlots;
    
    return Array.from({ length: numSlots }, (_, index) => {
      const segmentX = -jointWidthPx / 2 + index * segmentWidthPx;
      const segmentId = `${jointId}_segment_${index}`;
      const isWallSegmentActive = activeWallSegments && activeWallSegments.includes(index);
      
      return (
        <rect
          key={segmentId}
          x={segmentX}
          y={-totalInteractionHeightPx / 2}
          width={segmentWidthPx}
          height={totalInteractionHeightPx}
          fill="transparent"
          className="cursor-pointer"
          data-object-id={jointId}
          data-object-type={OBJECT_TYPES.DOCKING_JOINT}
          data-joint-x={joint.x}
          data-joint-y={joint.y}
          data-joint-width={width}
          data-joint-rotation={joint.rotation || 0}
          data-sandwich-width={sandwichWidthM}
          data-final-offset={finalOffset / scale}
          data-segment-index={index}
          data-segment-width={segmentWidthPx / scale}
          onClick={(e) => {
            e.stopPropagation();
            if (isWallSegmentActive) {
              const wallSegmentId = `${jointId}_wall_segment_${index}`;
              onSelectJoint(wallSegmentId);
            } else {
              onSelectJoint(jointId);
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onContextMenu(e, jointId, OBJECT_TYPES.DOCKING_JOINT);
          }}
        />
      );
    });
  };

  return (
    <g
      transform={jointTransform}
      data-object-id={jointId}
      data-object-type={OBJECT_TYPES.DOCKING_JOINT}
      data-joint-x={joint.x}
      data-joint-y={joint.y}
      data-joint-width={width}
      data-joint-rotation={joint.rotation || 0}
      data-sandwich-width={sandwichWidthM}
      data-final-offset={finalOffset / scale}
    >
      {renderPhysicalWall()}
      
      {renderJointSegments()}
      
      {overlayFill !== "none" && (
        <rect
          x={-jointWidthPx / 2}
          y={-totalInteractionHeightPx / 2}
          width={jointWidthPx}
          height={totalInteractionHeightPx}
          fill={overlayFill}
          stroke={isSelected ? SELECTED_ELEMENT_COLOR : "none"}
          strokeWidth={isSelected ? 1.5 : 0}
          pointerEvents="none"
        />
      )}

      {renderElements()}
    </g>
  );
};

export default React.memo(DockingJointRenderer);